import { ref, type Ref } from 'vue'
import { normalizeDeckResponse } from '../../data/decksApi'
import { isRecord, wpFetchWithCtx, type PFCAdminContext } from '../../data/wpApi'
import { coerceFlowGraphSnapshot } from '../model/graphCodec'
import { deckUrl, flowGraphUrl } from '../model/restUrls'
import type { FlowGraphV1, FlowSnapshot, GroupOption } from '../model/types'

const parseJson = (text: string): unknown => {
  try {
    return JSON.parse(text) as unknown
  } catch {
    return null
  }
}

/** First detail of a structured REST validation error, as shown to the author. */
const describeSaveError = (json: unknown, status: number) => {
  if (!isRecord(json) || !isRecord(json.error) || typeof json.error.code !== 'string') {
    return `Save failed (${status}).`
  }
  const details = json.error.details
  const firstDetail = Array.isArray(details) && details.length ? details[0] : null
  return typeof firstDetail === 'object' && firstDetail
    ? `Save failed: ${JSON.stringify(firstDetail)}`
    : `Save failed (${status}).`
}

/**
 * Loading and saving a deck's flow graph through the flashcards REST API. Status
 * messages are shown next to the save button; a failed load leaves the default graph.
 */
export function useFlowPersistence(
  wpCtx: Ref<PFCAdminContext | null>,
  groupOptions: Ref<GroupOption[]>,
  load: (snap: FlowSnapshot) => void,
) {
  const remoteStatus = ref<string | null>(null)
  const remoteUpdatedAt = ref<string | null>(null)

  /** Replaces placeholder group options with the deck's real groups, when it has any. */
  const loadDeckGroups = async (ctx: PFCAdminContext) => {
    try {
      const response = await wpFetchWithCtx(ctx, deckUrl(ctx), { method: 'GET' })
      if (!response.ok) return
      const deck = normalizeDeckResponse(await response.json())
      if (!deck?.groups?.length) return
      const groups = deck.groups
        .filter((g) => typeof g.id === 'string' && g.id.trim().length > 0)
        .map((g) => ({ id: g.id!, title: g.title || g.id! }))
      if (groups.length > 0) groupOptions.value = groups
    } catch {
      // Non-fatal: fall back to the default placeholder groups.
    }
  }

  const loadFromWordPress = async (ctx: PFCAdminContext) => {
    remoteStatus.value = 'Loading from WordPress…'
    await loadDeckGroups(ctx)
    try {
      const response = await wpFetchWithCtx(ctx, flowGraphUrl(ctx), { method: 'GET' })
      const json = parseJson(await response.text())
      if (!response.ok || !isRecord(json) || json.ok !== true) {
        remoteStatus.value = `Load failed (${response.status}).`
        return
      }
      remoteUpdatedAt.value = typeof json.updatedAt === 'string' ? json.updatedAt : null
      if (json.flowGraph == null) {
        remoteStatus.value = null
        return
      }
      const snap = coerceFlowGraphSnapshot(json.flowGraph)
      if (!snap) {
        remoteStatus.value = 'Load failed (invalid flow graph returned).'
        return
      }
      load(snap)
      remoteStatus.value = null
    } catch (error) {
      console.warn('Failed to load flow graph', error)
      remoteStatus.value = 'Load failed (network error).'
    }
  }

  /** Saves the graph; resolves to whether WordPress accepted it. */
  const saveToWordPress = async (ctx: PFCAdminContext, flowGraph: FlowGraphV1) => {
    remoteStatus.value = 'Saving to WordPress…'
    try {
      const response = await wpFetchWithCtx(ctx, flowGraphUrl(ctx), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flowGraph }),
      })
      const json = parseJson(await response.text())
      if (response.ok && isRecord(json) && json.ok === true) {
        remoteUpdatedAt.value = typeof json.updatedAt === 'string' ? json.updatedAt : null
        remoteStatus.value = null
        return true
      }
      remoteStatus.value = describeSaveError(json, response.status)
      return false
    } catch (error) {
      console.warn('Failed to save flow graph', error)
      remoteStatus.value = 'Save failed (network error).'
      return false
    }
  }

  return { remoteStatus, remoteUpdatedAt, loadDeckGroups, loadFromWordPress, saveToWordPress }
}
